import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { renewContractCustomer } from '@/modules/Contract/services/customer'
import { CustomerContract } from '@/modules/Contract/types'
import { ModalProps } from '@/types/shared'
import { formatPGDate } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { schemaRenewContractModal } from './schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface RenewContractFormValues {
    contract_effective_date: string | null
    contract_expiry_date: string | null
    renewal_reason?: string
}

export interface IRenewContractProps extends ModalProps {
    contractID: string
    contract: CustomerContract
}

const RenewContractModal = ({
    onClose,
    open,
    contractID,
    contract,
}: IRenewContractProps) => {
    console.log(contract, contractID)
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'contracts',
        mode: 'contains',
    })

    const formControl = useForm<RenewContractFormValues>({
        defaultValues: {
            contract_effective_date: contract?.contract_effective_date || null,
            contract_expiry_date: contract?.contract_expiry_date || null,
            renewal_reason: '',
        },
        resolver: yupResolver(schemaRenewContractModal as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: renewContractCustomer,
        },
        {
            successMessage: t('renew-contract-success'),
            reactQueryKeys: QUERY_KEYS.CONTRACTS,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const onSubmit = async (values: RenewContractFormValues) => {
        await mutateAsync({
            contractID: Number(contractID),
            data: {
                contract_effective_date: values.contract_effective_date
                    ? formatPGDate(values.contract_effective_date, 'YYYY-MM-DD')
                    : null,
                contract_expiry_date: values.contract_expiry_date
                    ? formatPGDate(values.contract_expiry_date, 'YYYY-MM-DD')
                    : null,
                renewal_reason: values.renewal_reason,
            },
        })
    }

    return (
        <Dialog title={t('renew_contract')} onClose={onClose} open={open}>
            <GridHookForm
                className="!gap-2"
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('renew_contract'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        formItemProps: {
                            name: 'contract_effective_date',
                            type: 'datepicker',
                            label: t('contract_effective_date'),
                            placeholder: t('contract_effective_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'contract_expiry_date',
                            type: 'datepicker',
                            label: t('contract_expiry_date'),
                            placeholder: t('contract_expiry_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'renewal_reason',
                            type: 'textarea',
                            label: t('renewal_reason'),
                            placeholder: t('renewal_reason_placeholder'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default RenewContractModal
